import React, { useState } from "react";

import { useToaster } from "utils";
import { TaskForm, TaskFormValues, TaskResource } from "./TaskForm";
import { IssueApi, issuesApi } from "api/issuesApi";
import { ISSUE_ACTIVITY_TYPES, TASKS_NAMES, TASKS_STATUSES } from "consts/tasks";
import { UserAvatar } from "components/molecules";
import { PaziaModal } from "components/molecules";
import { formatDate } from "utils";
import { TaskDate } from "pages/Tasks/tasks/TaskCardFooterStatus";
import { TaskCardItemsList } from "pages/Tasks/tasks/TaskCardItemsList";
import { ALL_ISSUES_QUERY, ARCHIVED_ISSUES_QUERY, useIssue } from "api/hooks";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { NewCommentForm } from "./NewCommentForm";
import { htmlParser } from "utils/htmlParser";
import { queryClient } from "main";
import { ModalHeader } from "../Modals/ModalHeader";

interface PreviewTaskModalProps {
  onCloseRequest: () => void;
  issueId?: number;
  resource?: TaskResource | null;
}

export const PreviewTaskModal = ({ onCloseRequest, issueId, resource }: PreviewTaskModalProps) => {
  const { showSuccess, showError } = useToaster();
  const { data, loading, refresh } = useIssue(issueId);
  const [editMode, setEditMode] = React.useState(false);

  const onSuccessSubmit = async (data: TaskFormValues) => {
    if (!issueId) return;
    try {
      await issuesApi.update(issueId, data);
      showSuccess("Zaktualizowano zadanie");
      refreshIssue();
      setEditMode(false);
      onCloseRequest();
    } catch (e) {
      showError("Błąd podczas aktualizacji zadania");
      console.error(e);
    }
  };

  const onArchive = async () => {
    if (!issueId) return;
    try {
      await issuesApi.changeStatus(issueId, TASKS_STATUSES.ARCHIVED);
      showSuccess("Zarchiwizowano zadanie");
      refreshIssue();
      setEditMode(false);
      onCloseRequest();
    } catch (e) {
      showError("Błąd podczas archiwizacji zadania");
      console.error(e);
    }
  };

  const refreshIssue = () => {
    refresh();
    queryClient.invalidateQueries({ queryKey: ALL_ISSUES_QUERY });
    queryClient.invalidateQueries({ queryKey: ARCHIVED_ISSUES_QUERY });
  };

  return (
    <PaziaModal
      id="edit-issue-modal"
      onCloseRequest={() => {
        onCloseRequest();
        setEditMode(false);
      }}
      isOpen={issueId != null}
      size="5xl"
      className="h-[80vh] w-2/3 overflow-hidden"
    >
      <div className="h-full">
        {loading ? (
          <PaziaLoader />
        ) : (
          <>
            {data && (
              <>
                {editMode ? (
                  <div className="p-4">
                    <ModalHeader className="flex items-center justify-between">
                      Edycja zadania
                      <div className="flex gap-4">
                        <PaziaButton title="Wróć" type="tertiary" onClick={() => setEditMode(false)} />
                      </div>
                    </ModalHeader>

                    <TaskForm onSuccessSubmit={onSuccessSubmit} issue={data} />
                  </div>
                ) : (
                  <IssuePreview
                    issue={data}
                    onEdit={() => setEditMode(true)}
                    refresh={refreshIssue}
                    onArchive={onArchive}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </PaziaModal>
  );
};

interface IssuePreviewProps {
  issue: IssueApi;
  onEdit: () => void;
  refresh: () => void;
  onArchive: () => void;
}

const IssuePreview = ({ issue, onEdit, refresh, onArchive }: IssuePreviewProps) => {
  const [activeTab, setActiveTab] = useState<"comments" | "activities">("comments");
  return (
    <div className="h-full p-4">
      <ModalHeader className="flex items-center justify-between">
        {issue.title}{" "}
        <div className="flex gap-4">
          <PaziaButton title="Archiwizuj" type="danger2" onClick={onArchive} />
          <PaziaButton title="Edytuj" type="tertiary" onClick={onEdit} />
        </div>
      </ModalHeader>
      <div className="grid grid-cols-2" style={{ height: "calc(100% - 70px)" }}>
        <div className="flex flex-col gap-4 px-4">
          <div>
            <div className="font-semibold">Opis</div>
            <div className="text-sm">
              {issue.description ? (
                issue.description
              ) : (
                <span className="italic">Brak opisu, edytuj żeby uzupełnić.</span>
              )}
            </div>
          </div>
          <div>
            <div className="font-semibold">Status</div>
            <div className="flex items-end gap-2">
              <IssueStatus status={issue.status} />
              <IssueStatusActions status={issue.status} issueId={issue.id} refresh={refresh} />
            </div>
          </div>
          {issue.issueItems && issue.issueItems?.length > 0 && (
            <div>
              <div className="font-semibold">Lista zadań</div>
              <div>
                <TaskCardItemsList items={issue.issueItems} separator={false} />
              </div>
            </div>
          )}

          <div>
            <div className="font-semibold">Należy wykonać do</div>
            <div>{issue.dueDate ? <TaskDate date={issue.dueDate} /> : <span>Nie ustawiono</span>}</div>
          </div>
          <div>
            <div className="font-semibold">Utworzone</div>
            <div className="flex items-center gap-2">
              <UserAvatar firstName={issue.createdBy.firstName} surname={issue.createdBy.surname} />{" "}
              {issue.createdBy.firstName} {issue.createdBy.surname} - {formatDate(issue.createdDate, true, true)}
            </div>
          </div>
          <div>
            <div className="font-semibold">Przypisane do</div>
            {issue.assignees?.map((assignee) => (
              <div className="flex items-center gap-2" key={assignee.id}>
                <UserAvatar firstName={assignee.firstName} surname={assignee.surname} /> {assignee.firstName}{" "}
                {assignee.surname}
              </div>
            ))}
          </div>
        </div>
        <div className="overflow-auto pr-2" style={{ height: "calc(80vh - 90px)" }}>
          <div className="font-semibold">Historia</div>
          <div className="flex flex-col gap-4">
            <NewCommentForm onSubmit={refresh} issueId={issue.id} />
            <ul className={`inline-flex flex-wrap text-sm font-medium`}>
              {[
                {
                  name: "comments" as const,
                  label: "Komentarze",
                },
                {
                  name: "activities" as const,
                  label: "Wszystkie aktywności",
                },
              ].map((tab) => (
                <li
                  key={tab.name}
                  className={`after:content-['/'] last:after:hidden ${
                    tab.name === activeTab
                      ? "text-primary-500"
                      : "hover:text-primary-500 dark:hover:text-primary-500 cursor-pointer"
                  } after:px-2 after:text-slate-400 dark:after:text-slate-600`}
                  onClick={() => setActiveTab(tab.name)}
                >
                  {tab.label}
                </li>
              ))}
            </ul>
            {issue.activities
              ?.filter((activity) => (activeTab === "comments" ? activity.type === ISSUE_ACTIVITY_TYPES.Comment : true))
              .map((activity) => (
                <div
                  key={activity.issueActivityId}
                  className="relative rounded-xl border bg-slate-100 px-4 pb-1 pt-7 text-sm dark:bg-slate-700"
                >
                  <div className="absolute left-2 top-2 text-xs text-slate-400">
                    {formatDate(activity.createdDate, true, true)}
                  </div>
                  {htmlParser.toHTML(activity.description)}
                  <div className="text-end text-sm font-medium text-slate-500">{activity.author.email}</div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

interface IssueStatusActionsProps {
  status: number;
  issueId: number;
  refresh: (newStatus: number) => void;
}
const IssueStatusActions = ({ status, issueId, refresh }: IssueStatusActionsProps) => {
  const [processing, setProcessing] = useState(false);

  const done = async () => await issuesApi.changeStatus(issueId, TASKS_STATUSES.DONE);
  const inProgress = async () => await issuesApi.changeStatus(issueId, TASKS_STATUSES.INPROGRESS);
  const todo = async () => await issuesApi.changeStatus(issueId, TASKS_STATUSES.TODO);

  const changeStatus = async (apiCall: () => Promise<void>, newStatus: number) => {
    try {
      setProcessing(true);
      await apiCall();
      refresh(newStatus);
    } catch (e) {
      console.error(e);
    } finally {
      setProcessing(false);
    }
  };

  const todoBtn = { title: "Do zrobienia", type: "tertiary", action: todo, status: TASKS_STATUSES.TODO };
  const inProgressBtn = { title: "W trakcie", type: "yellow2", action: inProgress, status: TASKS_STATUSES.INPROGRESS };
  const doneBtn = { title: "Zakończ", type: "success2", action: done, status: TASKS_STATUSES.DONE };

  const matrix = {
    [TASKS_STATUSES.TODO]: [inProgressBtn, doneBtn],
    [TASKS_STATUSES.INPROGRESS]: [todoBtn, doneBtn],
    [TASKS_STATUSES.DONE]: [todoBtn, inProgressBtn],
    [TASKS_STATUSES.ARCHIVED]: [],
  };

  return (
    <>
      {/* @ts-ignore */}
      {matrix[status].map((btn) => (
        <PaziaButton
          key={btn.title}
          title={btn.title}
          type={btn.type}
          className="p-1"
          onClick={() => changeStatus(btn.action, btn.status)}
          disabled={processing}
        />
      ))}
    </>
  );
};

const IssueStatus = ({ status }: { status: number }) => {
  return (
    <div className="flex gap-2">
      <IssueStatusDot status={status} />
      {/* @ts-ignore */}
      <span>{TASKS_NAMES[status]}</span>
    </div>
  );
};

const IssueStatusDot = ({ status }: { status: number }) => {
  switch (status) {
    case TASKS_STATUSES.INPROGRESS:
      return <span className="text-yellow-500">●</span>;
    case TASKS_STATUSES.DONE:
      return <span className="text-green-500">●</span>;
    case TASKS_STATUSES.TODO:
      return <span className="text-gray-500">●</span>;
    default:
      return <span className="text-gray-500">●</span>;
  }
};
